// src/pages/MyProfilePage.js
import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import ChangePassword from "../components/UserManagement/ChangePassword";
import { roleLabel, roleClass } from "../constants/roles";
import "./MyProfilePage.css";

function getInitials(name = "", email = "") {
  const n = String(name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    const a = (parts[0]?.[0] || "").toUpperCase();
    const b = (parts[1]?.[0] || "").toUpperCase();
    return (a + b) || a || "U";
  }
  const e = String(email || "").trim();
  return (e[0] || "U").toUpperCase();
}

export default function MyProfilePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview"); // overview | security

  const profile = useMemo(() => {
    const u = user || {};
    return {
      name: u.name || "User",
      email: u.email || "",
      role: (u.role || "analyst").toLowerCase(),
      mustChangePassword: !!u.mustChangePassword,
      id: u.id || u.userId || "",
    };
  }, [user]);

  const initials = useMemo(
    () => getInitials(profile.name, profile.email),
    [profile.name, profile.email]
  );

  return (
    <div className="mp-page">
      <div className="mp-card">
        <div className="mp-head">
          <div className="mp-titlewrap">
            <h1 className="mp-title">My Profile</h1>
            <p className="mp-subtitle">
              View your account details and manage security settings.
            </p>
          </div>

          <div className="mp-badges">
            <span className={`mp-badge ${roleClass(profile.role)}`}>
              {roleLabel(profile.role)}
            </span>

            {profile.mustChangePassword ? (
              <span className="mp-badge warn">Action required</span>
            ) : (
              <span className="mp-badge ok">Secure</span>
            )}
          </div>
        </div>

        <div className="mp-tabs">
          <button
            type="button"
            className={`mp-tab ${tab === "overview" ? "active" : ""}`}
            onClick={() => setTab("overview")}
          >
            Overview
          </button>

          <button
            type="button"
            className={`mp-tab ${tab === "security" ? "active" : ""}`}
            onClick={() => setTab("security")}
          >
            Security
          </button>
        </div>

        <div className="mp-body">
          {tab === "overview" ? (
            <div className="mp-grid">
              <div className="mp-panel">
                <div className="mp-profileRow">
                  <div className="mp-avatar" aria-hidden>
                    {initials}
                  </div>

                  <div className="mp-profileMeta">
                    <div className="mp-name">{profile.name}</div>
                    <div className="mp-email">{profile.email || "—"}</div>
                    {profile.id ? <div className="mp-id">User ID: {profile.id}</div> : null}
                  </div>
                </div>

                <div className="mp-kv">
                  <div className="mp-kvRow">
                    <div className="mp-k">Role</div>
                    <div className="mp-v">{roleLabel(profile.role)}</div>
                  </div>

                  <div className="mp-kvRow">
                    <div className="mp-k">Password status</div>
                    <div className="mp-v">
                      {profile.mustChangePassword ? (
                        <span className="mp-pill warn">Must change password</span>
                      ) : (
                        <span className="mp-pill ok">Up to date</span>
                      )}
                    </div>
                  </div>

                  <div className="mp-kvRow">
                    <div className="mp-k">Account</div>
                    <div className="mp-v">
                      <span className="mp-pill neutral">Active</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          ) : (
            <div className="mp-security">
              <div className="mp-securityHeader">
                <div>
                  <h3 className="mp-panelTitle">Change password</h3>
                </div>

                {profile.mustChangePassword ? (
                  <span className="mp-pill warn">Required</span>
                ) : (
                  <span className="mp-pill ok">Optional</span>
                )}
              </div>
                            <div className="mp-panel mp-panel-secondary">
                <h3 className="mp-panelTitle">Security recommendations</h3>
                <ul className="mp-list">
                  <li>Use a unique password that you do not reuse elsewhere.</li>
                  <li>Avoid sharing temporary passwords in chat. Use a secure channel.</li>
                  <li>Change your password immediately if you suspect compromise.</li>
                </ul>

                <div className="mp-callout">
                  <div className="mp-calloutTitle">Tip</div>
                  <div className="mp-calloutText">
                    You can update your password anytime from the <b>Security</b> tab.
                  </div>
                </div>
              </div>


              {/* Embed your existing ChangePassword component */}
              <div className="mp-embed">
                <ChangePassword />
              </div>
            </div>
          )}
        </div>
        
      </div>
      
    </div>
  );
}
